import Geomagnetism from '@cristianob/geomagnetism'
import * as Turf from '@turf/turf'

import { Coordinates } from '@/domain/protocols/Coordinates'

const geomagnetismModel = Geomagnetism.model()

/**
 * Corrects the magnetic declination of a given heading
 *
 * @param heading Heading in circular degrees (North 0, positive clockwise)
 * @param startCoords Coordinates of the start point
 * @returns The heading corrected with the Magnetic Declination
 */
export const fixHeadingByMagneticDeclination = (heading: number, startCoords: Coordinates, invert?: boolean) => {
  let newHeading
  const declination = geomagnetismModel.point([startCoords.latitude, startCoords.longitude])

  if (invert) {
    newHeading = heading + declination.decl
  } else {
    newHeading = heading - declination.decl
  }

  if (newHeading <= 0) {
    newHeading = 360 + newHeading
  }

  if (newHeading > 360) {
    newHeading = newHeading - 360
  }

  return newHeading
}

export const coordinateFromRadialDistance = (
  start: Coordinates,
  magneticRadial: number,
  distanceNm: number
): Coordinates => {
  const trueBearing = fixHeadingByMagneticDeclination(magneticRadial, start, true)
  const dest = Turf.destination(Turf.point(start.toArray()), distanceNm, trueBearing, { units: 'nauticalmiles' })
  const [lng, lat] = dest.geometry.coordinates

  return Coordinates.create({ latitude: lat, longitude: lng })
}

/**
 * Calculates the geographic heading between two coordinates
 *
 * @param start Coordinates of start point
 * @param end Coordinates of end point
 * @returns Magnetic Heading between the two coordinates, corrected by the
 * magnetic declination
 */
export const calculateHeading = (start: Coordinates, end: Coordinates) => {
  const startPoint = Turf.point(start.toArray())
  const endPoint = Turf.point(end.toArray())

  const geographicBearing = Turf.bearing(startPoint, endPoint)
  const azimuthBearing = Turf.bearingToAzimuth(geographicBearing)

  return azimuthBearing
}

/**
 * Calculates the magnetic heading between two coordinates
 *
 * @param start Coordinates of start point
 * @param end Coordinates of end point
 * @returns Magnetic Heading between the two coordinates, corrected by the
 * magnetic declination
 */
export const calculateMagneticHeading = (start: Coordinates, end: Coordinates, invert?: boolean) => {
  return fixHeadingByMagneticDeclination(calculateHeading(start, end), start, invert)
}

/**
 * Converts a decimal coordinate (number) in a DMS format(string)
 *
 * @param coordinate Decimal coordinates
 * @param threeDegreesDigits Displays the result with a 3-digit degrees (with zero padding)
 * @returns Coordinate in DMS format
 */
export const toDegreesMinutesAndSeconds = (coordinate: number, threeDegreesDigits: boolean = false): string => {
  const absolute = Math.abs(coordinate)
  let degrees = Math.floor(absolute)
  const minutesNotTruncated = (absolute - degrees) * 60
  let minutes = Math.floor(minutesNotTruncated)
  let seconds = Math.round((minutesNotTruncated - minutes) * 60)

  if (seconds === 60) {
    minutes += 1
    seconds = 0
  }

  if (minutes === 60) {
    degrees += 1
    minutes = 0
  }

  if (degrees > 180 && threeDegreesDigits) {
    degrees = degrees - 360
  }

  if (degrees > 90 && !threeDegreesDigits) {
    degrees = degrees - 180
  }

  const padDegrees = `0${degrees}`.slice(threeDegreesDigits ? -3 : -2)
  const padMinutes = `0${minutes}`.slice(-2)
  const padSeconds = `0${seconds}`.slice(-2)

  return `0${padDegrees}${padMinutes}${padSeconds}`.slice(threeDegreesDigits ? -7 : -6)
}

/**
 * Converts both latitude and longitude in a unique string
 * in a DMS format
 *
 * @param latitude Decimal latitude
 * @param longitude Decimal longitude
 * @returns A string formatted in DMS of both lat and lng
 */
export const convertDecimalDegreesToDMS = (latitude: number, longitude: number): string => {
  const lat = toDegreesMinutesAndSeconds(latitude)
  const latitudeCardinal = latitude >= 0 ? 'N' : 'S'

  const long = toDegreesMinutesAndSeconds(longitude, true)
  const longitudeCardinal = longitude >= 0 ? 'E' : 'W'

  return `${lat}${latitudeCardinal} ${long}${longitudeCardinal}`
}
/**
 * Converts both latitude and longitude in a unique string
 * in a DMS format, ignoring seconds
 * @param latitude Decimal latitude
 * @param longitude Decimal longitude
 * @returns A string formatted in DMS of both lat and lng without seconds
 */
export const convertDecimalDegreesToDMSWithoutSeconds = (latitude: number, longitude: number): string => {
  const lat = toDegreesMinutesAndSeconds(latitude)
  const latitudeCardinal = latitude >= 0 ? 'N' : 'S'

  const long = toDegreesMinutesAndSeconds(longitude, true)
  const longitudeCardinal = longitude >= 0 ? 'E' : 'W'

  return `${lat.slice(0, -2)}${latitudeCardinal}${long.slice(0, -2)}${longitudeCardinal}`
}

/**
 * Convert DMS To Decimal Degrees
 *
 * @param dms A string formatted in DMS
 * @returns An lat and lng in degrees
 */
export const DMSToDecimal = (dms: RegExpExecArray): Coordinates => {
  const lat = parseInt(dms[1]) + (parseInt(dms[2]) || 0) / 60 + (parseInt(dms[3]) || 0) / 3600
  if (dms.length === 5) {
    return Coordinates.create({
      latitude: ['n', 'N'].includes(dms[4]) ? lat : -lat,
      longitude: 0
    })
  }
  const lng = parseInt(dms[5]) + (parseInt(dms[6]) || 0) / 60 + (parseInt(dms[7]) || 0) / 3600
  return Coordinates.create({
    latitude: ['n', 'N'].includes(dms[4]) ? lat : -lat,
    longitude: ['E', 'e', 'L', 'l'].includes(dms[8]) ? lng : -lng
  })
}

const isValidCoordinateRange = (latitude: number, longitude: number) => {
  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
}

const createCoordinatesIfValid = (latitude: number, longitude: number): Coordinates | null => {
  if (!isValidCoordinateRange(latitude, longitude)) return null

  return Coordinates.create({ latitude, longitude })
}

const parseCoordinateNumber = (value: string) => {
  return parseFloat(value.replace(',', '.'))
}

const matchCompactCoordinateInput = (input: string) => {
  return input
    .trim()
    .toUpperCase()
    .match(/^(\d{6}[NS])[\s,;/]+(\d{7}[EOWL])$/)
}

const splitCompactCoordinatePart = (raw: string, isLatitude: boolean) => {
  const hemisphere = raw.slice(-1)
  const digits = raw.slice(0, -1)
  const degreesLength = isLatitude ? 2 : 3

  return {
    hemisphere,
    degrees: parseInt(digits.slice(0, degreesLength), 10),
    minutes: parseInt(digits.slice(degreesLength, degreesLength + 2), 10),
    seconds: parseInt(digits.slice(degreesLength + 2), 10)
  }
}

const parseCompactCoordinateInput = (input: string): Coordinates | null => {
  const match = matchCompactCoordinateInput(input)
  if (!match) return null

  const parseCompactPart = (raw: string, isLatitude: boolean) => {
    const { hemisphere, degrees, minutes, seconds } = splitCompactCoordinatePart(raw, isLatitude)

    let value = degrees + minutes / 60 + seconds / 3600

    if (['S', 'W', 'O'].includes(hemisphere)) {
      value *= -1
    }

    return value
  }

  const latitude = parseCompactPart(match[1], true)
  const longitude = parseCompactPart(match[2], false)

  return createCoordinatesIfValid(latitude, longitude)
}

/**
 * Matches the hemisphere-suffixed formats common in aviation, with optional decimals (dot or comma):
 * - decimal degrees: `20.5S 054.3W`
 * - degrees and decimal minutes: `2033.5S 05430.6W`
 *
 * The number of integer digits tells them apart: latitude has 1–2 digits of degrees or 4 digits (DDMM),
 * longitude has 1–3 digits of degrees or 5 digits (DDDMM). 6/7 digits (DDMMSS) are the compact format.
 */
const matchHemisphereCoordinateInput = (input: string) => {
  return input
    .trim()
    .toUpperCase()
    .match(/^(\d{1,2}|\d{4})(?:[.,](\d+))?\s*([NS])[\s,;/]+(\d{1,3}|\d{5})(?:[.,](\d+))?\s*([EOWL])$/)
}

const splitHemisphereCoordinatePart = (
  integerDigits: string,
  decimalDigits: string | undefined,
  isLatitude: boolean
) => {
  const degreesLength = isLatitude ? 2 : 3
  const fraction = decimalDigits ? parseFloat(`0.${decimalDigits}`) : 0

  if (integerDigits.length <= degreesLength) {
    return { degrees: parseInt(integerDigits, 10) + fraction, minutes: 0 }
  }

  return {
    degrees: parseInt(integerDigits.slice(0, degreesLength), 10),
    minutes: parseInt(integerDigits.slice(degreesLength), 10) + fraction
  }
}

const parseHemisphereCoordinateInput = (input: string): Coordinates | null => {
  const match = matchHemisphereCoordinateInput(input)
  if (!match) return null

  const latitudePart = splitHemisphereCoordinatePart(match[1], match[2], true)
  const longitudePart = splitHemisphereCoordinatePart(match[4], match[5], false)

  const latitudeSignal = match[3] === 'S' ? -1 : 1
  const longitudeSignal = ['W', 'O'].includes(match[6]) ? -1 : 1

  return createCoordinatesIfValid(
    (latitudePart.degrees + latitudePart.minutes / 60) * latitudeSignal,
    (longitudePart.degrees + longitudePart.minutes / 60) * longitudeSignal
  )
}

const parseDecimalCoordinateInput = (input: string): Coordinates | null => {
  const match = input.trim().match(/^\(?\s*([+-]?\d+(?:[.,]\d+)?)\s*[,;/\s]\s*([+-]?\d+(?:[.,]\d+)?)\s*\)?$/)
  if (!match) return null

  const latitude = parseCoordinateNumber(match[1])
  const longitude = parseCoordinateNumber(match[2])

  return createCoordinatesIfValid(latitude, longitude)
}

const matchDMSCoordinateInput = (input: string) => {
  const normalizedInput = input.trim().toUpperCase()
  const dmsPattern =
    /^(\d{1,2})(?:\s*[º°]\s*|\s+)(\d{1,2})(?:\s*['’′]\s*|\s+)(\d{1,2}(?:[.,]\d+)?)(?:\s*["”″]\s*)?\s*([NS])\s*[,;/\s]+\s*(\d{1,3})(?:\s*[º°]\s*|\s+)(\d{1,2})(?:\s*['’′]\s*|\s+)(\d{1,2}(?:[.,]\d+)?)(?:\s*["”″]\s*)?\s*([EOWL])$/

  return normalizedInput.match(dmsPattern)
}

const parseDMSCoordinateInput = (input: string): Coordinates | null => {
  const match = matchDMSCoordinateInput(input)
  if (!match) return null

  const latitude = parseInt(match[1], 10) + parseInt(match[2], 10) / 60 + parseCoordinateNumber(match[3]) / 3600
  const longitude = parseInt(match[5], 10) + parseInt(match[6], 10) / 60 + parseCoordinateNumber(match[7]) / 3600

  const latitudeSignal = match[4] === 'S' ? -1 : 1
  const longitudeSignal = ['W', 'O'].includes(match[8]) ? -1 : 1

  return createCoordinatesIfValid(latitude * latitudeSignal, longitude * longitudeSignal)
}

export const parseUserCoordinateInput = (input: string): Coordinates | null => {
  if (!input?.trim()) return null

  return (
    parseCompactCoordinateInput(input) ??
    parseHemisphereCoordinateInput(input) ??
    parseDecimalCoordinateInput(input) ??
    parseDMSCoordinateInput(input) ??
    null
  )
}

export const isUserCoordinateInput = (input: string) => {
  return parseUserCoordinateInput(input) !== null
}

export const normalizeUserCoordinateInput = (input: string): string | null => {
  const coordinates = parseUserCoordinateInput(input)
  if (!coordinates) return null

  return convertDecimalDegreesToDMS(coordinates.latitude, coordinates.longitude)
}

/**
 * Checks whether a valid user coordinate input has minutes or seconds of 60 or more
 * (e.g. `236060S 0466060W` or `2360.5S 04630.0W`). Such input is accepted, but `normalizeUserCoordinateInput`
 * carries the excess over to the next minute/degree (`240100S 0470100W`).
 *
 * @param input Coordinate typed by the user, in any format accepted by `parseUserCoordinateInput`
 * @returns `true` when the input is valid and has minutes or seconds >= 60
 */
export const hasMinutesOrSecondsOverflow = (input: string): boolean => {
  if (!parseUserCoordinateInput(input)) return false

  const isOverflow = (value: number) => value >= 60

  const compactMatch = matchCompactCoordinateInput(input)
  if (compactMatch) {
    return [splitCompactCoordinatePart(compactMatch[1], true), splitCompactCoordinatePart(compactMatch[2], false)].some(
      ({ minutes, seconds }) => isOverflow(minutes) || isOverflow(seconds)
    )
  }

  const hemisphereMatch = matchHemisphereCoordinateInput(input)
  if (hemisphereMatch) {
    return [
      splitHemisphereCoordinatePart(hemisphereMatch[1], hemisphereMatch[2], true),
      splitHemisphereCoordinatePart(hemisphereMatch[4], hemisphereMatch[5], false)
    ].some(({ minutes }) => isOverflow(minutes))
  }

  const dmsMatch = matchDMSCoordinateInput(input)
  if (dmsMatch) {
    return [dmsMatch[2], dmsMatch[3], dmsMatch[6], dmsMatch[7]].some((value) =>
      isOverflow(parseCoordinateNumber(value))
    )
  }

  return false
}

/**
 * Calculates the distance in degrees between two points with high precision
 *
 * @variation degreesPythagoreanDistance
 * This function have a slow performance for more than 40.000 registers (250ms in a medium 2021 hardware).
 * For bigger arrays, use degreesPythagoreanDistance (55ms for 40.000 registers).
 *
 * @param start Coordinates of start point
 * @param end Coordinates of end point
 * @returns The distance in degrees between two points
 */
export const degreesDistance = (start: Coordinates, end: Coordinates) => {
  const startPoint = Turf.point(start.toArray())
  const endPoint = Turf.point(end.toArray())
  return Turf.distance(startPoint, endPoint, { units: 'degrees' })
}

/**
 * Calculates the distance in nautical miles between two points with high precision
 *
 * @variation nmPythagoreanDistance
 * This function have a slow performance for more than 40.000 registers (250ms in a medium 2021 hardware).
 * For bigger arrays, use nmPythagoreanDistance (55ms for 40.000 registers).
 *
 * @param start Coordinates of start point
 * @param end Coordinates of end point
 * @returns The distance in nm between two points
 */
export const nmDistance = (start: Coordinates, end: Coordinates) => {
  const startPoint = Turf.point(start.toArray())
  const endPoint = Turf.point(end.toArray())
  return Turf.distance(startPoint, endPoint, { units: 'nauticalmiles' })
}

/**
 * Calculates the distance in kilometers between two points with high precision
 *
 * @variation kmPythagoreanDistance
 * This function have a slow performance for more than 40.000 registers (250ms in a medium 2021 hardware).
 * For bigger arrays, use kmPythagoreanDistance (55ms for 40.000 registers).
 *
 * @param start Coordinates of start point
 * @param end Coordinates of end point
 * @returns The distance in km between two points
 */
export const kmDistance = (start: Coordinates, end: Coordinates) => {
  const startPoint = Turf.point(start.toArray())
  const endPoint = Turf.point(end.toArray())
  return Turf.distance(startPoint, endPoint, { units: 'kilometers' })
}

/**
 * Calculates the distance in degrees between two points with low precision
 *
 * @warning This function has a poor precision for coordinates
 * far from the equator. DO NOT USE for important measures.
 * @variation degreesDistance for important measures.
 *
 * @param start Coordinates of start point
 * @param end Coordinates of end point
 * @returns The distance in degrees between two points
 */
export const degreesPythagoreanDistance = (coords1: Coordinates, coords2: Coordinates) => {
  return Math.sqrt(
    Math.pow(coords2.longitude - coords1.longitude, 2) + Math.pow(coords2.latitude - coords1.latitude, 2)
  )
}

/**
 * Calculates the distance in nautical miles between two points with low precision
 *
 * @warning This function has a poor precision for coordinates
 * far from the equator. DO NOT USE for important measures.
 * @variation nmDistance for important measures.
 *
 * @param start Coordinates of start point
 * @param end Coordinates of end point
 * @returns The distance in nm between two points
 */
export const nmPythagoreanDistance = (coords1: Coordinates, coords2: Coordinates) => {
  const hypotenuse = Math.sqrt(
    Math.pow(coords2.longitude - coords1.longitude, 2) + Math.pow(coords2.latitude - coords1.latitude, 2)
  )

  return hypotenuse * 60
}

/**
 * Calculates the distance in kilometers between two points with low precision
 *
 * @warning This function has a poor precision for coordinates
 * far from the equator. DO NOT USE for important measures.
 * @variation kmDistance for important measures.
 *
 * @param start Coordinates of start point
 * @param end Coordinates of end point
 * @returns The distance in km between two points
 */
export const kmPythagoreanDistance = (start: Coordinates, end: Coordinates) => {
  const hypotenuse = Math.sqrt(
    Math.pow(end.longitude - start.longitude, 2) + Math.pow(end.latitude - start.latitude, 2)
  )

  return hypotenuse * 111
}

/**
 * Calculates the distance in nautical miles between two points and a line segment
 * (a line between two points)
 *
 * @param coord Coordinates of the point point
 * @param l1 Coordinates of the start point of the line segment
 * @param l2 Coordinates of the end point of the line segment
 * @returns The distance in nautical miles between two points and a line segment
 */
export const distanceCoordToSegment = (coord: Coordinates, l1: Coordinates, l2: Coordinates) => {
  if (l1.latitude === l2.latitude && l1.longitude === l2.longitude) {
    return nmDistance(coord, l1)
  }

  const point = Turf.point(coord.toArray())
  const line = Turf.lineString([l1.toArray(), l2.toArray()])

  return Turf.pointToLineDistance(point, line, { units: 'nauticalmiles' })
}

/**
 * Calculate Zoom Level of the map by a given boundaries
 *
 * @param corners The corners
 * @param mapWidth Width of the map view
 * @param mapHeight Height of the map view
 * @returns The map zoom that fit the boundaries
 */
export const getZoomByItemCorners = (
  corners: [Coordinates, Coordinates, Coordinates, Coordinates],
  mapWidth: number,
  mapHeight: number
): number => {
  const WORLD_DIM = { height: 256, width: 256 }
  const ZOOM_MAX = 20

  const getBoundsByCorners = (
    corners: [Coordinates, Coordinates, Coordinates, Coordinates]
  ): {
    ne: Coordinates
    sw: Coordinates
  } => {
    const minLat = Math.min(...corners.map((corner) => corner.latitude))
    const maxLat = Math.max(...corners.map((corner) => corner.latitude))
    const minLng = Math.min(...corners.map((corner) => corner.longitude))
    const maxLng = Math.max(...corners.map((corner) => corner.longitude))

    return { ne: Coordinates.createFromArray([maxLng, maxLat]), sw: Coordinates.createFromArray([minLng, minLat]) }
  }

  const latRad = (lat: number) => {
    const sin = Math.sin((lat * Math.PI) / 180)
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
  }

  const zoom = (mapPx: number, worldPx: number, fraction: number) => {
    return Math.log(mapPx / worldPx / fraction) / Math.LN2
  }

  const { ne, sw } = getBoundsByCorners(corners)

  const latFraction = (latRad(ne.latitude) - latRad(sw.latitude)) / Math.PI

  const lngDiff = ne.longitude - sw.longitude
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360

  const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction)
  const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFraction)

  return Math.min(latZoom, lngZoom, ZOOM_MAX)
}

/**
 * Rounds a heading to the nearest whole degree, in the 1–360 range (north is 360, aviation convention)
 *
 * @param heading Heading in degrees, any range
 * @returns The rounded heading, from 1 to 360
 */
export const roundHeadingDegrees = (heading: number): number => {
  const rounded = Math.round(heading) % 360
  const normalized = rounded < 0 ? rounded + 360 : rounded

  return normalized === 0 ? 360 : normalized
}

/**
 * Calculates the magnetic bearing between two coordinates, rounded to whole degrees.
 * Single source for every displayed leg bearing (map labels, route panel, briefing PDF),
 * so the same leg never shows different values.
 *
 * @param start Coordinates of start point
 * @param end Coordinates of end point
 * @returns Magnetic bearing from 1 to 360 (north is 360)
 */
export const magneticBearing = (start: Coordinates, end: Coordinates) => {
  return roundHeadingDegrees(calculateMagneticHeading(start, end))
}

export const getBearingString = (bearing: number) => {
  let bearingString: string

  if (!bearing) return '---°'

  if (bearing >= 10 && bearing < 100) {
    bearingString = `0${bearing}`
  } else if (bearing < 10) {
    bearingString = `00${bearing}`
  } else {
    bearingString = bearing.toString()
  }

  return `${bearingString}°`
}
